import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ResponseAPIs = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="api-header-section">
        <div className="api-header-content">
          <div className="api-category-badge">
            <i className="fa-solid fa-code"></i>
            <span>API Reference</span>
          </div>
          <h1>Response APIs</h1>
          <p className="api-header-description">
            MemU Response API provides a unified conversational AI endpoint with built-in memory management,
            offering a complete solution for AI-powered conversations without manual memory operations.
          </p>
        </div>
        <div className="api-header-icon">
          <i className="fa-solid fa-comments"></i>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="authentication">Authentication</h2>
        <p>All Response API requests require authentication using your API key. Include your API key in the request headers:</p>
        
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {`curl -X POST https://api.memu.so/api/v2/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="base-url">Base URL</h2>
        <p>All API requests should be made to:</p>
        <div className="code-block">
          <SyntaxHighlighter
            language="text"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            https://api.memu.so/api/v2
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="chat-response">Chat API</h2>
        <p>Send a message and receive an AI response with automatic memory management.</p>
        
        <div className="api-endpoint">
          <span className="http-method post">POST</span>
          <span className="endpoint-url">/api/v2/chat</span>
        </div>

        <div className="feature-highlight-box">
          <h4>🚀 Key Benefits</h4>
          <ul>
            <li><strong>All-in-One:</strong> Single endpoint for complete conversational AI</li>
            <li><strong>Automatic Memory:</strong> No manual memorize/retrieve operations needed</li>
            <li><strong>Context Aware:</strong> System intelligently manages conversation history</li>
            <li><strong>Simple Integration:</strong> Minimal setup for maximum functionality</li>
          </ul>
        </div>

        <h3>Request Body</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "user_id": "user_123",
  "user_name": "John Doe",
  "agent_id": "agent_456",
  "agent_name": "Tennis Coach AI",
  "message": "Hello, can you remind me what we discussed about my tennis practice last week?",
  "system": "You are a helpful tennis coach assistant.",
  "model": "gpt-4o-mini",
  "kwargs": {
    "temperature": 0.7,
    "max_tokens": 1000
  }
}`}
          </SyntaxHighlighter>
        </div>

        <h3>Parameters</h3>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>user_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the user</td>
              </tr>
              <tr>
                <td><code>user_name</code></td>
                <td>string</td>
                <td>No</td>
                <td>Display name for the user</td>
              </tr>
              <tr>
                <td><code>agent_id</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Unique identifier for the AI agent</td>
              </tr>
              <tr>
                <td><code>agent_name</code></td>
                <td>string</td>
                <td>No</td>
                <td>Display name for the AI agent</td>
              </tr>
              <tr>
                <td><code>message</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>The user's message/query to the AI</td>
              </tr>
              <tr>
                <td><code>system</code></td>
                <td>string</td>
                <td>No</td>
                <td>System instructions for the AI agent</td>
              </tr>
              <tr>
                <td><code>model</code></td>
                <td>string</td>
                <td>No</td>
                <td>AI model to use (default: "gpt-4o-mini"). See <a href="/pricing#response-api">pricing page</a> for all available models</td>
              </tr>
              <tr>
                <td><code>kwargs</code></td>
                <td>object</td>
                <td>No</td>
                <td>Additional model parameters (e.g., temperature, max_tokens, top_p)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>kwargs Parameters</h3>
        <p>The <code>kwargs</code> object accepts additional model configuration parameters:</p>
        <div className="parameter-table">
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Default</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>temperature</code></td>
                <td>float</td>
                <td>0.7</td>
                <td>Controls randomness (0-2). Higher values make output more random</td>
              </tr>
              <tr>
                <td><code>max_tokens</code></td>
                <td>integer</td>
                <td>1000</td>
                <td>Maximum tokens in the response</td>
              </tr>
              <tr>
                <td><code>top_p</code></td>
                <td>float</td>
                <td>1.0</td>
                <td>Nucleus sampling parameter (0-1)</td>
              </tr>
              <tr>
                <td><code>frequency_penalty</code></td>
                <td>float</td>
                <td>0</td>
                <td>Reduces repetition (-2.0 to 2.0)</td>
              </tr>
              <tr>
                <td><code>presence_penalty</code></td>
                <td>float</td>
                <td>0</td>
                <td>Encourages new topics (-2.0 to 2.0)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Response (200 OK)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`"I remember from our conversation last week that you were working on improving your backhand technique. You mentioned you had a lesson scheduled and wanted to focus on follow-through. How did that practice session go?"`}
          </SyntaxHighlighter>
        </div>

        <p className="response-note">
          <strong>Note:</strong> The response is returned as a plain string containing the AI's reply message.
        </p>

        <h3>Example Request</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`curl -X POST https://api.memu.so/api/v2/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user_123",
    "user_name": "John Doe",
    "agent_id": "agent_456",
    "agent_name": "Tennis Coach AI",
    "message": "Hello, can you remind me what we discussed about my tennis practice last week?",
    "system": "You are a helpful tennis coach assistant.",
    "model": "gpt-4o-mini",
    "kwargs": {
      "temperature": 0.7,
      "max_tokens": 1000
    }
  }'`}
          </SyntaxHighlighter>
        </div>

        <h3>Example with Python</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="python"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`import requests

url = "https://api.memu.so/api/v2/chat"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}
payload = {
    "user_id": "user_123",
    "user_name": "John Doe",
    "agent_id": "agent_456",
    "agent_name": "Tennis Coach AI",
    "message": "What should I focus on in my next practice?",
    "system": "You are a helpful tennis coach assistant.",
    "model": "gpt-4o-mini",
    "kwargs": {
        "temperature": 0.7,
        "max_tokens": 1000
    }
}

response = requests.post(url, headers=headers, json=payload)
print(response.text)  # AI's response as a string`}
          </SyntaxHighlighter>
        </div>

        <h3>Example with JavaScript</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="javascript"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`const response = await fetch('https://api.memu.so/api/v2/chat', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    user_id: 'user_123',
    user_name: 'John Doe',
    agent_id: 'agent_456',
    agent_name: 'Tennis Coach AI',
    message: 'What should I focus on in my next practice?',
    system: 'You are a helpful tennis coach assistant.',
    model: 'gpt-4o-mini',
    kwargs: {
      temperature: 0.7,
      max_tokens: 1000
    }
  })
});

const aiResponse = await response.text();
console.log(aiResponse);  // AI's response as a string`}
          </SyntaxHighlighter>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="best-practices">Best Practices</h2>
        <div className="feature-highlight-box">
          <h4>🎯 Optimization Tips</h4>
          <ul>
            <li><strong>Consistent IDs:</strong> Use consistent user_id and agent_id across sessions for better memory continuity</li>
            <li><strong>Model Selection:</strong> Choose appropriate models based on response quality vs cost trade-offs</li>
            <li><strong>System Prompts:</strong> Provide clear system prompts to guide AI behavior and personality</li>
            <li><strong>Temperature Control:</strong> Use lower temperatures (0.3-0.7) for factual responses, higher (0.7-1.2) for creative tasks</li>
          </ul>
        </div>

        <div className="feature-highlight-box">
          <h4>💡 Memory Management</h4>
          <ul>
            <li><strong>Automatic Storage:</strong> All conversations are automatically stored for future context</li>
            <li><strong>Smart Retrieval:</strong> System intelligently retrieves relevant memories for each request</li>
            <li><strong>Context Window:</strong> Memory system extends effective context beyond model limits</li>
            <li><strong>Privacy Control:</strong> Each user_id has isolated memory space</li>
          </ul>
        </div>

        <div className="feature-highlight-box">
          <h4>⚙️ kwargs Configuration</h4>
          <ul>
            <li><strong>Temperature:</strong> Start with 0.7 and adjust based on output consistency needs</li>
            <li><strong>Max Tokens:</strong> Set appropriate limits to control costs and response length</li>
            <li><strong>Penalties:</strong> Use frequency_penalty to reduce repetition, presence_penalty for diverse topics</li>
            <li><strong>Top-p:</strong> Alternative to temperature for controlling randomness (typically use one or the other)</li>
          </ul>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="error-handling">Error Handling</h2>
        <p>The Response API uses standard HTTP status codes and returns detailed error messages.</p>

        <h3>Common Error Codes</h3>
        <div className="api-overview-table">
          <table>
            <thead>
              <tr>
                <th>Status Code</th>
                <th>Error Type</th>
                <th>Description</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>400</code></td>
                <td>Bad Request</td>
                <td>Invalid request parameters</td>
                <td>Check required fields (message, user_id, agent_id)</td>
              </tr>
              <tr>
                <td><code>401</code></td>
                <td>Unauthorized</td>
                <td>Missing or invalid API key</td>
                <td>Verify your API key in Authorization header</td>
              </tr>
              <tr>
                <td><code>422</code></td>
                <td>Validation Error</td>
                <td>Request validation failed</td>
                <td>Check parameter types and values</td>
              </tr>
              <tr>
                <td><code>429</code></td>
                <td>Rate Limit</td>
                <td>Too many requests</td>
                <td>Implement request throttling or upgrade plan</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Server Error</td>
                <td>Internal server error</td>
                <td>Retry request with exponential backoff</td>
              </tr>
              <tr>
                <td><code>503</code></td>
                <td>Service Unavailable</td>
                <td>Temporary service disruption</td>
                <td>Wait and retry, check status page</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Validation Error Response (422)</h3>
        <div className="code-block">
          <SyntaxHighlighter
            language="json"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: '2.5rem 1rem 1rem 1rem',
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`{
  "detail": [
    {
      "loc": ["body", "message"],
      "msg": "field required",
      "type": "value_error.missing"
    }
  ]
}`}
          </SyntaxHighlighter>
        </div>

        <div className="feature-highlight-box">
          <h4>⚡ Error Handling Tips</h4>
          <ul>
            <li><strong>Retry Logic:</strong> Implement exponential backoff for 429 and 5xx errors</li>
            <li><strong>Timeout Handling:</strong> Set appropriate timeouts for API calls (recommended: 30s)</li>
            <li><strong>Graceful Degradation:</strong> Provide fallback responses when API is unavailable</li>
            <li><strong>Monitoring:</strong> Log errors and track API response times for debugging</li>
            <li><strong>Validation:</strong> Validate required fields client-side before sending requests</li>
          </ul>
        </div>
      </div>

    </div>
  );
};

export default ResponseAPIs;